import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { z } from 'zod';
import DOMPurify from 'isomorphic-dompurify';

// Password validation schema
const LoginSchema = z.object({
  email: z.string().email('Invalid email address').trim().toLowerCase(),
  password: z.string().min(6, 'Password must be at least 6 characters')
});

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);

  const modalRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Focus email input when modal opens
  useEffect(() => {
    if (isOpen && emailInputRef.current) {
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Clear errors when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setLocalError('');
      clearError();
    }
  }, [isOpen, clearError]);

  // Handle escape key and click outside
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node) && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isSubmitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setLocalError('');
    clearError();

    // Rate limiting
    if (loginAttempts >= 5) {
      setLocalError('Too many login attempts. Please wait a few minutes before trying again.');
      return;
    }
    
    try {
      // Sanitize and validate input
      const sanitizedEmail = DOMPurify.sanitize(email.trim());
      const sanitizedPassword = DOMPurify.sanitize(password);
      
      const validatedData = LoginSchema.parse({ 
        email: sanitizedEmail, 
        password: sanitizedPassword 
      });
      
      setIsSubmitting(true);
      
      console.log('🔍 LoginModal: Starting login process...');
      await login(validatedData.email, validatedData.password);
      
      console.log('✅ LoginModal: Login successful, closing modal...');
      
      // Reset form and close modal
      setEmail('');
      setPassword('');
      setLoginAttempts(0);
      onClose();
      
      // Navigate to home
      navigate('/home', { replace: true });
      
    } catch (err) {
      console.error('❌ LoginModal: Login failed:', err);
      
      setLoginAttempts(prev => prev + 1);
      
      if (err instanceof z.ZodError) {
        setLocalError(err.errors[0].message);
      } else if (err instanceof Error) {
        setLocalError(err.message);
      } else {
        setLocalError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const displayError = localError || error;
  const isFormDisabled = isSubmitting || isLoading || loginAttempts >= 5;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="w-full max-w-md bg-[#272727] rounded-lg shadow-lg"
        role="dialog"
        aria-labelledby="login-title"
        aria-describedby="login-description"
      >
        <div className="p-6">
          <h2 id="login-title" className="text-2xl font-semibold text-white mb-2">
            Sign In
          </h2>
          <p id="login-description" className="text-gray-400 text-sm mb-6">
            Enter your credentials to access your account
          </p>

          {displayError && (
            <div className="mb-4 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{displayError}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email Address
              </label>
              <input
                ref={emailInputRef}
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 border border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter your email"
                required
                autoComplete="email"
                maxLength={100}
                disabled={isFormDisabled}
                aria-describedby={displayError ? "error-message" : undefined}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 border border-gray-700 pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  maxLength={100}
                  disabled={isFormDisabled}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={isFormDisabled}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                onClick={onClose}
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isFormDisabled || !email.trim() || !password.trim()}
              className="w-full py-2 px-4 bg-purple-500 text-white rounded-lg hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting || isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {loginAttempts >= 3 && loginAttempts < 5 && (
            <div className="mt-4 text-sm text-yellow-400 text-center">
              Warning: {5 - loginAttempts} attempts remaining
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;